import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, TextField, Button, Chip, Box, CircularProgress, makeStyles, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Link,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { tutorLeadService } from '../services';

const useStyles = makeStyles(() => ({
  header: { fontWeight: 600, marginBottom: 4 },
  sub: { color: '#6b7280', fontSize: 13, marginBottom: 16 },
  filterRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  searchRow: { display: 'flex', gap: 12, marginBottom: 20, maxWidth: 520 },
  filterChip: { cursor: 'pointer' },
  tutor: { fontWeight: 600, color: '#111827' },
  muted: { color: '#6b7280', fontSize: 13 },
  mobile: { fontFamily: 'monospace', fontSize: 13 },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 24 },
  statusSelect: { fontSize: 13, minWidth: 104 },
}));

type Lead = {
  id: string;
  tutorName: string;
  parentName: string;
  parentMobile?: string | null;
  subject?: string | null;
  classLabel?: string | null;
  area?: string | null;
  mode?: string | null;
  status: 'open' | 'notified' | 'closed';
  requestCount: number;
  clientName?: string | null;
  lastRequestedOn?: string | null;
};

type Counts = { open: number; notified: number; closed: number };

const STATUS_TABS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'notified', label: 'Notified' },
  { key: 'closed', label: 'Closed' },
];

const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const TutorLeadsScreen: React.FC = () => {
  const classes = useStyles();
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(30);
  const [counts, setCounts] = useState<Counts>({ open: 0, notified: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await tutorLeadService.list(status, search, page, 30);
    if (res.ok) {
      setLeads(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setLimit(res.data?.limit || 30);
      if (res.data?.counts) setCounts(res.data.counts);
    }
    setLoading(false);
  }, [status, search, page]);

  useEffect(() => { load(); }, [load]);

  const applySearch = () => { setPage(1); setSearch(input.trim()); };
  const pickStatus = (key: string) => { setPage(1); setStatus(key); };
  const totalPages = Math.max(1, Math.ceil(total / (limit || 30)));

  const handleSetStatus = async (lead: Lead, next: 'open' | 'notified' | 'closed') => {
    if (!next || next === lead.status) return;
    setBusyId(lead.id);
    const res = await tutorLeadService.setStatus(lead.id, next);
    setBusyId(null);
    if (res.ok) {
      // Reload so the counts + (if filtered) the row's membership refresh.
      load();
    } else {
      window.alert(res.error || 'Failed to update status');
    }
  };

  const countFor = (key: string) =>
    key === 'open' ? counts.open : key === 'notified' ? counts.notified : key === 'closed' ? counts.closed : (counts.open + counts.notified + counts.closed);

  return (
    <div>
      <Typography variant="h5" className={classes.header}>Tutor Leads</Typography>
      <Typography className={classes.sub}>
        "Notify me when available" requests from parents who hit an empty slot picker. The demand signal — which tutor, subject &amp; area — so you know where to onboard.
      </Typography>

      <div className={classes.filterRow}>
        {STATUS_TABS.map((tab) => (
          <Chip
            key={tab.key || 'all'}
            className={classes.filterChip}
            color={status === tab.key ? 'primary' : 'default'}
            variant={status === tab.key ? 'default' : 'outlined'}
            label={`${tab.label} · ${countFor(tab.key)}`}
            onClick={() => pickStatus(tab.key)}
          />
        ))}
      </div>

      <div className={classes.searchRow}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search tutor, parent, subject, area…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
        />
        <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={applySearch}>Search</Button>
      </div>

      {loading ? (
        <Box p={6} display="flex" justifyContent="center"><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tutor</TableCell>
                  <TableCell>Parent</TableCell>
                  <TableCell>Subject / Class</TableCell>
                  <TableCell>Area</TableCell>
                  <TableCell align="center">Asks</TableCell>
                  <TableCell>Last asked</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} hover>
                    <TableCell><span className={classes.tutor}>{l.tutorName}</span></TableCell>
                    <TableCell>
                      <div>{l.parentName}</div>
                      {l.parentMobile ? (
                        <Link className={classes.mobile} href={`tel:${l.parentMobile}`}>{l.parentMobile}</Link>
                      ) : <span className={classes.muted}>—</span>}
                    </TableCell>
                    <TableCell>
                      {[l.subject, l.classLabel].filter(Boolean).join(' · ') || <span className={classes.muted}>—</span>}
                    </TableCell>
                    <TableCell>{l.area || <span className={classes.muted}>—</span>}</TableCell>
                    <TableCell align="center">{l.requestCount}</TableCell>
                    <TableCell><span className={classes.muted}>{fmtDate(l.lastRequestedOn)}</span></TableCell>
                    <TableCell>
                      <Select
                        className={classes.statusSelect}
                        value={l.status}
                        disableUnderline
                        disabled={busyId === l.id}
                        onChange={(e) => handleSetStatus(l, e.target.value as 'open' | 'notified' | 'closed')}
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="notified">Notified</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
                {leads.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="textSecondary">No leads match this filter yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <div className={classes.pager}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Typography variant="body2">Page {page} / {totalPages}</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </>
      )}
    </div>
  );
};

export default TutorLeadsScreen;
